import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { X, Mic, Keyboard, Square, Loader2, Check, RotateCcw, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'idee', labelKey: 'capture.cat.idee' },
  { value: 'decision', labelKey: 'capture.cat.decision' },
  { value: 'signal_rh', labelKey: 'capture.cat.signal_rh' },
  { value: 'opportunite', labelKey: 'capture.cat.opportunite' },
  { value: 'inquietude', labelKey: 'capture.cat.inquietude' },
  { value: 'autre', labelKey: 'capture.cat.autre' },
];

const MAX_RECORD_SEC = 180;

const CaptureModal = ({ onClose }) => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [phase, setPhase] = useState('choose'); // choose | recording | writing | validating | saved
  const [text, setText] = useState('');
  const [source, setSource] = useState('texte');
  const [categorie, setCategorie] = useState('autre');
  const [catLoading, setCatLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recordTime, setRecordTime] = useState(0);
  const [showCatMenu, setShowCatMenu] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const maxTimerRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  }, []);

  useEffect(() => { return cleanup; }, [cleanup]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => handleRecordingStop();
      recorder.start();
      mediaRecorderRef.current = recorder;
      setPhase('recording');
      setSource('vocal');
      setRecordTime(0);
      timerRef.current = setInterval(() => setRecordTime(prev => prev + 1), 1000);
      maxTimerRef.current = setTimeout(() => stopRecording(), MAX_RECORD_SEC * 1000);
    } catch (err) {
      toast.error(t('capture.micError'));
    }
  };

  const stopRecording = () => {
    cleanup();
    // onstop handler will process
  };

  const handleRecordingStop = async () => {
    setPhase('validating');
    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    if (blob.size < 1000) {
      toast.error(t('capture.tooShort'));
      setPhase('choose');
      return;
    }
    // Transcribe via existing STT
    const formData = new FormData();
    formData.append('audio_file', blob, 'capture.webm');
    try {
      const res = await api.post('/api/voice/stt', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const transcribed = res.data.text || '';
      if (!transcribed.trim()) {
        toast.error(t('capture.noSpeech'));
        setPhase('choose');
        return;
      }
      setText(transcribed);
      categorizeText(transcribed);
    } catch (err) {
      toast.error(t('capture.sttError'));
      setPhase('choose');
    }
  };

  const categorizeText = async (content) => {
    setCatLoading(true);
    try {
      const res = await api.post('/api/capture/categorize', { text: content });
      setCategorie(res.data.categorie || 'autre');
    } catch { setCategorie('autre'); }
    finally { setCatLoading(false); }
  };

  const handleWriteSubmit = () => {
    if (!text.trim()) return;
    setSource('texte');
    setPhase('validating');
    categorizeText(text);
  };

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/capture/save', { contenu_texte: text.trim(), categorie, source });
      setPhase('saved');
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      toast.error(t('common.error'));
    } finally { setSaving(false); }
  };

  const handleRestart = () => {
    setText('');
    setCategorie('autre');
    setPhase('choose');
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const getCatLabel = (val) => {
    const found = CATEGORIES.find(c => c.value === val);
    return found ? t(found.labelKey) : val;
  };

  return (
    <div data-testid="capture-modal" className="fixed inset-0 z-50 bg-atlasBg/95 backdrop-blur-sm flex flex-col animate-in slide-in-from-bottom duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div>
          {phase !== 'saved' && <h2 className="text-lg font-semibold text-white">{t('capture.title')}</h2>}
          {phase === 'choose' && <p className="text-sm text-atlasTextSecondary">{t('capture.subtitle')}</p>}
        </div>
        <button data-testid="capture-close-btn" onClick={onClose} className="p-2 rounded-lg text-atlasTextSecondary hover:text-white hover:bg-white/5">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Choose mode */}
      {phase === 'choose' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
          <button data-testid="capture-voice-btn" onClick={startRecording}
            className="w-full max-w-sm py-6 rounded-2xl bg-atlasCyan/10 border border-atlasCyan/30 flex flex-col items-center gap-3 hover:bg-atlasCyan/15 transition-all">
            <Mic className="w-10 h-10 text-atlasCyan" />
            <span className="text-white font-medium">{t('capture.speakToAtlas')}</span>
          </button>
          <button data-testid="capture-text-btn" onClick={() => setPhase('writing')}
            className="w-full max-w-sm py-6 rounded-2xl bg-white/5 border border-atlasBorder flex flex-col items-center gap-3 hover:bg-white/10 transition-all">
            <Keyboard className="w-10 h-10 text-atlasTextSecondary" />
            <span className="text-white font-medium">{t('capture.write')}</span>
          </button>
        </div>
      )}

      {/* Recording */}
      {phase === 'recording' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-8 px-6">
          <p className="text-atlasTextSecondary">{t('capture.listening')}</p>
          {/* Wave animation */}
          <div className="relative w-32 h-32 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" style={{ animationDuration: '1.5s' }} />
            <div className="absolute inset-4 rounded-full bg-red-500/30 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="w-16 h-16 rounded-full bg-red-500/40 flex items-center justify-center">
              <Mic className="w-8 h-8 text-white" />
            </div>
          </div>
          <p className="text-2xl font-mono text-white">{formatTime(recordTime)}</p>
          <button data-testid="capture-stop-btn" onClick={stopRecording}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center transition-all shadow-lg">
            <Square className="w-6 h-6 text-white fill-white" />
          </button>
        </div>
      )}

      {/* Writing */}
      {phase === 'writing' && (
        <div className="flex-1 flex flex-col px-6 py-4">
          <textarea data-testid="capture-textarea" value={text} onChange={e => setText(e.target.value)}
            placeholder={t('capture.placeholder')} autoFocus
            className="flex-1 bg-transparent border-none text-white text-lg resize-none focus:outline-none placeholder:text-atlasTextSecondary leading-relaxed" />
          <div className="pt-4">
            <Button data-testid="capture-validate-text-btn" onClick={handleWriteSubmit} disabled={!text.trim()}
              className="w-full btn-primary py-3">{t('capture.validate')}</Button>
          </div>
        </div>
      )}

      {/* Validating */}
      {phase === 'validating' && (
        <div className="flex-1 flex flex-col px-6 py-4">
          <textarea data-testid="capture-edit-text" value={text} onChange={e => setText(e.target.value)}
            className="flex-1 bg-atlasCard border border-atlasBorder rounded-xl p-4 text-white text-base resize-none focus:outline-none focus:border-atlasCyan/50 leading-relaxed mb-4" />
          
          {/* Category */}
          <div className="mb-4 relative">
            <p className="text-xs text-atlasTextSecondary mb-2">{t('capture.category')}</p>
            <button data-testid="capture-cat-btn" onClick={() => setShowCatMenu(!showCatMenu)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-atlasCard border border-atlasBorder text-white text-sm hover:border-yellow-500/50 transition-all">
              {catLoading ? <Loader2 className="w-4 h-4 animate-spin text-yellow-400" /> : <span>{getCatLabel(categorie)}</span>}
              <ChevronDown className="w-4 h-4 text-atlasTextSecondary" />
            </button>
            {showCatMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-atlasCard border border-atlasBorder rounded-xl overflow-hidden shadow-xl z-10">
                {CATEGORIES.map(c => (
                  <button key={c.value} onClick={() => { setCategorie(c.value); setShowCatMenu(false); }}
                    data-testid={`capture-cat-${c.value}`}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${categorie === c.value ? 'bg-yellow-500/10 text-yellow-400' : 'text-white hover:bg-white/5'}`}>
                    {t(c.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleRestart} className="flex-1 border-atlasTextSecondary text-atlasTextSecondary">
              <RotateCcw className="w-4 h-4 mr-2" />{t('capture.restart')}
            </Button>
            <Button data-testid="capture-save-btn" onClick={handleSave} disabled={saving || !text.trim()}
              className="flex-1 btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />{t('capture.save')}</>}
            </Button>
          </div>
        </div>
      )}

      {/* Saved */}
      {phase === 'saved' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFD60A' }}>
            <Check className="w-8 h-8 text-gray-900" />
          </div>
          <p className="text-lg font-semibold text-white text-center">{t('capture.savedMessage')}</p>
          <p className="text-sm text-atlasTextSecondary text-center">{t('capture.savedSub')}</p>
        </div>
      )}
    </div>
  );
};

export default CaptureModal;
